/**
 * @file useClients.ts
 * @description Custom hook for managing clients state.
 * Provides functionality to fetch, create, update, and delete clients.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { auth } from "../config/firebaseConfig";
import { Client } from "../types";
import {
  getClients as fetchClients,
  createClient as createClientApi,
  updateClient as updateClientApi,
  deleteClient as deleteClientApi,
} from "../services/clientService";
import log from "../config/logging";

// Global flag to prevent duplicate error toasts
let hasShownErrorToast = false;
const ERROR_TOAST_RESET_DELAY = 5000; // Reset flag after 5 seconds

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const loadClients = useCallback(async () => {
    // Check authentication before making request
    const user = auth.currentUser;
    if (!user) {
      log.debug("User not authenticated, skipping clients fetch");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchClients();
      setClients(data);
      hasLoadedRef.current = true;
      hasShownErrorToast = false; // Reset error flag on success
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load clients";
      setError(errorMessage);
      log.error("Error loading clients", { error: err });
      
      // Only show toast if we haven't shown one recently
      if (!hasShownErrorToast) {
        hasShownErrorToast = true;
        toast.error(errorMessage);
        // Reset flag after delay
        setTimeout(() => {
          hasShownErrorToast = false;
        }, ERROR_TOAST_RESET_DELAY);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for authentication before loading
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !hasLoadedRef.current) {
        loadClients();
      }
    });

    // Also try to load immediately if user is already authenticated
    if (auth.currentUser && !hasLoadedRef.current) {
      loadClients();
    }

    return () => unsubscribe();
  }, [loadClients]);

  const createClient = useCallback(
    async (name: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const clientId = await createClientApi(name);
        await loadClients();
        toast.success("Cliente creato con successo");
        return clientId;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create client";
        setError(errorMessage);
        log.error("Error creating client", { error: err });
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [loadClients]
  );

  const updateClient = useCallback(
    async (clientId: string, name: string) => {
      try {
        setIsLoading(true);
        setError(null);
        await updateClientApi(clientId, name);
        await loadClients();
        toast.success("Cliente aggiornato con successo");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update client";
        setError(errorMessage);
        log.error("Error updating client", { error: err });
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [loadClients]
  );

  const deleteClient = useCallback(
    async (clientId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        await deleteClientApi(clientId);
        await loadClients();
        toast.success("Cliente eliminato con successo");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete client";
        setError(errorMessage);
        log.error("Error deleting client", { error: err });
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [loadClients]
  );

  return {
    clients,
    isLoading,
    error,
    loadClients,
    createClient,
    updateClient,
    deleteClient,
  };
};
